import React from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useTracking } from 'react-tracking';
import { Text, styled } from '@applatform/design-system';
import Icon from 'components/V1/Icon/Icon';

const StyledLink = styled(Link, {
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  py: '$6',
  px: '$8',
  border: 0,
  color: '$secondaryGrey',
  textDecoration: 'none',
  outline: 0,
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  transition: 'all 200ms',
  '&:hover': {
    backgroundColor: '$violet100',
    color: '$secondary',
  },
  '&:focus': {
    backgroundColor: '$violet100',
    color: '$secondary',
  },
  svg: {
    mr: '$2',
  },

  variants: {
    isActive: {
      true: {
        color: '$secondary',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: '$transparent',
        },
        '&::before': {
          content: `''`,
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: '$1',
          backgroundColor: '$secondary',
        },
      },
    },
  },
});

interface NavLinkProps {
  readonly children: React.ReactNode;
  readonly icon: React.ComponentType;
  readonly onClick?: () => void;
  readonly to: string;
}

function NavLink({ children, icon, onClick, to }: NavLinkProps) {
  // `/` would otherwise match every route, so only it is matched exactly.
  const isActiveRoute = Boolean(useRouteMatch({ path: to, exact: to === '/' }));
  const { trackEvent } = useTracking();

  function handleClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    trackEvent({
      name: 'page_navigation',
      value: event.currentTarget.innerText,
    });
    onClick?.();
  }

  return (
    <StyledLink
      to={to}
      isActive={isActiveRoute}
      onClick={handleClick}
      aria-current={isActiveRoute ? 'page' : undefined}
    >
      <Icon as={icon} aria-hidden="true" />
      <Text>{children}</Text>
    </StyledLink>
  );
}

export default NavLink;
